import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { DateTime } from "luxon";
dayjs.extend(duration);
// Convert function:
export const convertTz = (datetime, fromTz) => {
  const toTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const format = "HH:mm";
  if (!datetime || !fromTz || fromTz === toTz) {
    return dayjs("1/1/1 " + datetime).format(format);
  }
  try {
    return DateTime.fromFormat(datetime, format, { zone: fromTz })
      .setZone(toTz)
      .toFormat(format);
  } catch (error) {
    return datetime;
  }
};

export function formatAndAddDaysToDateWithIntl(startDate, daysToAdd) {
  // Create a new Date object from the start date
  const date = new Date(getDateTimeDate(startDate));

  // Add the specified number of days
  date.setDate(date.getDate() + daysToAdd);

  // Create an Intl.DateTimeFormat object
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
    hour12: false,
  });

  // Format the date
  const [
    { value: month },
    ,
    { value: day },
    ,
    { value: year },
    ,
    { value: hour },
    ,
    { value: minute },
    ,
    { value: second },
  ] = formatter.formatToParts(date);

  // Construct the formatted string
  return `${year}-${month}-${day}T${hour}:${minute}:${second}Z`;
}

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export function calculateTimeDifference(time1, time2) {
  const [hours1, minutes1, seconds1] = time1?.split(":").map(Number);
  const [hours2, minutes2, seconds2] = time2?.split(":").map(Number);

  // Convert times to total minutes
  const totalMinutes1 = hours1 * 60 + minutes1 + (seconds1 || 0) / 60;
  const totalMinutes2 = hours2 * 60 + minutes2 + (seconds2 || 0) / 60;

  // Calculate the difference in minutes
  const differenceInMinutes = Math.round(totalMinutes1 - totalMinutes2);

  return differenceInMinutes;
}

export const getDifferenceFromNowInHours = (targetTime) => {
  // Parse the current time
  const now = dayjs();

  // Parse the target time using today's date
  const [hours, minutes, seconds] = targetTime?.split(":");
  const target = dayjs()
    .hour(hours)
    .minute(minutes)
    .second(seconds || 0);

  // Calculate the difference in milliseconds
  const diffInMilliseconds = now.diff(target);

  // Convert the difference to hours
  const diffInHours = dayjs.duration(diffInMilliseconds).asHours();

  return diffInHours;
};

export const convertTimeToMMHH = (time) => {
  return dayjs("1/1/25 " + time).format("h:mma");
};

export const convertTimeToMMHHWithTimezone = (time, timezone) => {
  const converted = convertTz(time, timezone);
  const date = dayjs("1/1/25 " + converted).format("h:mma");
  return date;
};

export const isNowBeforeTime = (t1, t2) => {
  return getTimesDifference(t1, t2) < 0;
};

export const getTimesDifference = (t1, t2) => {
  const d1 = new Date(`2025-12-12T${t1}`);
  const d2 = new Date(`2025-12-12T${t2}`);

  const mins1 = d1.getHours() * 60 + d1.getMinutes();
  const mins2 = d2.getHours() * 60 + d2.getMinutes();

  return mins1 - mins2;
};

export const itsMeetingTime = (date, startTime, endTime, closeTime) => {
  if (!date || !startTime || !endTime) return false;
  const itsToday = dayjs(getDateTimeDate(date)).isSame(
    dayjs().format("YYYY-MM-DD")
  );
  const meetingDuration = getTimesDifference(endTime, startTime);
  const meetingStartTimeAhead = getDifferenceFromNowInHours(startTime);
  // const meetingEndTimePassed = getDifferenceFromNowInHours(endTime);
  const minutesAhead = meetingStartTimeAhead * 60;
  // const minutesToEnding = meetingEndTimePassed * 60;

  // returns true when it's 5 minutes to start time or within meeting duration
  if (itsToday && minutesAhead >= -5 && minutesAhead <= meetingDuration) {
    return true;
  }
  return false;
};

export const getDateTimeDate = (date) => {
  return date ? date?.split("T")[0] : "";
};

export function getTimeToMeeting(meeting) {
  // Get the current date and time
  const now = new Date();

  // Parse the meeting start date and time
  const startDate = new Date(meeting.start_date);
  const [startHours, startMinutes] = meeting?.start_time
    ?.split(":")
    .map(Number);

  // Set the meeting start time to the correct hours and minutes
  startDate.setHours(startHours, startMinutes, 0, 0);

  // Calculate the difference in milliseconds
  let diffInMs = startDate - now;

  // If the meeting has already started or is in the past, return null or an appropriate message
  if (diffInMs <= 0) {
    return null; // or return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  // Convert the difference to days, hours, minutes, and seconds
  const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  diffInMs %= 1000 * 60 * 60 * 24; // Remaining time after days

  const hours = Math.floor(diffInMs / (1000 * 60 * 60));
  diffInMs %= 1000 * 60 * 60; // Remaining time after hours

  const minutes = Math.floor(diffInMs / (1000 * 60));
  diffInMs %= 1000 * 60; // Remaining time after minutes

  const seconds = Math.ceil(diffInMs / 1000); // Use Math.ceil to ensure seconds go from 1 to 60

  // Adjust if seconds are 60 (due to Math.ceil)
  if (seconds === 60) {
    return { D: days, H: hours, M: minutes + 1, S: 0 };
  }

  return { D: days, H: hours, M: minutes, S: seconds };
}
